import { isValidSortOrder } from "@/modules/ui/components/dataTables/datatableUtils"
import { DEFAULT_PAGE_SIZE } from "@/modules/utils/paginated"
import { BoxStaffItemEntity } from "@modules/boxStaff/entities/boxStaffEntity"
import { StaffColumnToSort } from "@modules/boxStaff/gateway/boxStaffDto"
import { isValidStaffSortBy } from "@modules/boxStaff/hooks/useBoxStaff"
import { useServicesContext } from "@modules/core/services/services.context"
import useDebounce from "@modules/hooks/useDebounce"
import { MemberItemEntity } from "@modules/members/entities/memberEntity"
import { MemberColumnToSort } from "@modules/members/gateway/memberDto"
import { isValidMemberSortBy } from "@modules/members/hooks/useMember"
import { ColumnSortingOrder } from "@modules/types/datatableTypes"
import { usePersistedDatatable } from "@modules/utils/hooks/useDataTable"
import { useSetMultiParams } from "@modules/utils/navigation/useQueryParam"
import { useSuspenseQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router"

export const useBoxArchivedMembers = ({
  nameFilter,
  page,
  pageSize,
  sortBy,
  sortOrder,
}: {
  nameFilter?: string
  page?: number
  pageSize?: number
  sortBy?: string
  sortOrder?: string
}) => {
  const { memberApi } = useServicesContext()

  const { data, isLoading } = useSuspenseQuery({
    queryKey: ["boxArchivedMembers", { page, pageSize, nameFilter, sortBy, sortOrder }],

    queryFn: async () => {
      if (sortBy && isValidMemberSortBy(sortBy) && sortOrder && isValidSortOrder(sortOrder)) {
        return await memberApi.getArchivedMembers(page, pageSize, nameFilter, sortBy, sortOrder)
      }
      return await memberApi.getArchivedMembers(page, pageSize, nameFilter)
    },
  })

  return {
    data: data.content.map((member) => MemberItemEntity.fromItemDto(member)) || [],
    totalElements: data.totalElements,
    isLoading,
    hasPreviousPage: !data.first,
    hasNextPage: !data.last,
  }
}

export const usePersistedBoxArchivedMembers = () => {
  const [searchParams] = useSearchParams()
  const setListParams = useSetMultiParams()

  const sortBy = searchParams.get("sortBy") ?? undefined
  const sortOrder = searchParams.get("order") ?? undefined
  const nameFilter = searchParams.get("nameFilter") ?? undefined

  const debouncedSearchTerm = useDebounce(nameFilter, 300)

  const { page, setPage, size: pageSize, setSize: setPageSize } = usePersistedDatatable(DEFAULT_PAGE_SIZE)

  const { data, isLoading, hasPreviousPage, hasNextPage, totalElements } = useBoxArchivedMembers({
    nameFilter: debouncedSearchTerm,
    page,
    pageSize,
    sortBy,
    sortOrder,
  })

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      setPage((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (hasNextPage) {
      setPage((prev) => prev + 1)
    }
  }

  const setSorting = (field: MemberColumnToSort, order: ColumnSortingOrder) => {
    if (field === sortBy && order === sortOrder) {
      setListParams([
        { key: "sortBy", value: undefined },
        { key: "order", value: undefined },
      ])
    } else {
      setListParams([
        { key: "sortBy", value: field },
        { key: "order", value: order },
      ])
    }
  }

  return {
    data,
    totalElements,
    isLoading,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage,
    hasNextPage,
    setSorting,
    sortBy,
    sortOrder,
    isSearching: !!nameFilter,
  }
}

export const useBoxArchivedStaffMembers = ({
  nameFilter,
  page,
  pageSize,
  sortBy,
  sortOrder,
}: {
  nameFilter?: string
  page?: number
  pageSize?: number
  sortBy?: string
  sortOrder?: string
}) => {
  const { memberApi } = useServicesContext()
  const { data, isLoading } = useSuspenseQuery({
    queryKey: ["boxArchivedStaffs", { page, pageSize, nameFilter, sortBy, sortOrder }],

    queryFn: async () => {
      if (sortBy && isValidStaffSortBy(sortBy) && sortOrder && isValidSortOrder(sortOrder)) {
        return await memberApi.getArchivedStaffMembers(page, pageSize, nameFilter, sortBy, sortOrder)
      }
      return await memberApi.getArchivedStaffMembers(page, pageSize, nameFilter)
    },
  })

  return {
    data: data.content.map((boxStaff) => BoxStaffItemEntity.fromItemDto(boxStaff)) || [],
    totalElements: data.totalElements,
    isLoading,
    hasPreviousPage: !data.first,
    hasNextPage: !data.last,
  }
}

export const usePersistedBoxArchivedStaffMembers = () => {
  const [searchParams] = useSearchParams()
  const setListParams = useSetMultiParams()

  const sortBy = searchParams.get("sortBy") ?? undefined
  const sortOrder = searchParams.get("order") ?? undefined
  const nameFilter = searchParams.get("nameFilter") ?? undefined

  const debouncedSearchTerm = useDebounce(nameFilter, 300)

  const { page, setPage, size: pageSize, setSize: setPageSize } = usePersistedDatatable(DEFAULT_PAGE_SIZE)

  const { data, isLoading, hasPreviousPage, hasNextPage, totalElements } = useBoxArchivedStaffMembers({
    nameFilter: debouncedSearchTerm,
    page,
    pageSize,
    sortBy,
    sortOrder,
  })

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      setPage((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (hasNextPage) {
      setPage((prev) => prev + 1)
    }
  }

  const setSorting = (field: StaffColumnToSort, order: ColumnSortingOrder) => {
    if (field === sortBy && order === sortOrder) {
      setListParams([
        { key: "sortBy", value: undefined },
        { key: "order", value: undefined },
      ])
    } else {
      setListParams([
        { key: "sortBy", value: field },
        { key: "order", value: order },
      ])
    }
  }

  return {
    data,
    totalElements,
    isLoading,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage,
    hasNextPage,
    setSorting,
    sortBy,
    sortOrder,
    isSearching: !!nameFilter,
  }
}
